import { useCallback, useEffect, useState } from 'react'
import { ApiError } from '@/lib/apiClient'
import { useApiClient } from '@/hooks/useApiClient'

export interface PersonaListItem {
  id: string
  title: string
  organizacionId: string | null
  organizacionNombre: string | null
  cargo: string | null
  email: string | null
  telefono: string | null
  rolEnRelacion: 'decisor' | 'influenciador' | 'operativo' | 'sponsor' | 'otro' | null
  outlookContactStatus: OutlookContactStatus
}

export type OutlookContactStatus = 'exists' | 'not_found' | 'sin_email' | 'unavailable'

export type PersonasStatus =
  | 'loading'
  | 'ready'
  | 'not_signed_in'
  | 'not_configured'
  | 'forbidden'
  | 'unauthorized'
  | 'error'

interface PersonasState {
  status: PersonasStatus
  personas: PersonaListItem[]
  error: ApiError | Error | null
  reload: () => void
}

function statusFromError(err: unknown): PersonasStatus {
  if (err instanceof ApiError) {
    if (err.status === 401) return 'unauthorized'
    if (err.status === 403) return 'forbidden'
    if (err.status === 503) return 'not_configured'
  }
  return 'error'
}

export function usePersonas(): PersonasState {
  const { gate, makeClient } = useApiClient()
  const [personas, setPersonas] = useState<PersonaListItem[]>([])
  const [status, setStatus] = useState<PersonasStatus>('loading')
  const [error, setError] = useState<ApiError | Error | null>(null)
  const [attempt, setAttempt] = useState(0)

  const reload = useCallback(() => setAttempt((v) => v + 1), [])

  useEffect(() => {
    if (gate !== 'ready') {
      setStatus(gate)
      return
    }

    const controller = new AbortController()
    const client = makeClient()
    if (!client) {
      setStatus('not_signed_in')
      return
    }

    setStatus('loading')
    setError(null)

    client
      .get<{ personas: PersonaListItem[] }>('/api/personas', { signal: controller.signal })
      .then((response) => {
        setPersonas(response.personas)
        setStatus('ready')
      })
      .catch((err: ApiError | Error) => {
        if (controller.signal.aborted) return
        setPersonas([])
        setError(err)
        setStatus(statusFromError(err))
      })

    return () => controller.abort()
  }, [gate, attempt, makeClient])

  return { status, personas, error, reload }
}

export interface PersonaDetail extends PersonaListItem {
  esPrincipal: boolean
  activo: boolean
  historial: Array<{ id: string; tipo: string; title: string; fecha: string; detalle: string | null; estado?: string }>
}

export type OutlookContactPlanMode = 'create' | 'link' | 'linked' | 'email_required' | 'ambiguous' | 'owner_required' | 'schema_not_ready' | 'uncertain'

export interface OutlookContactPreview {
  id: string
  title: string | null
  email: string | null
  cargo: string | null
  telefono: string | null
}

export interface OutlookContactPlan {
  mode: OutlookContactPlanMode
  contact?: OutlookContactPreview
  matches?: OutlookContactPreview[]
  linkId?: string
}

export function usePersonaOutlookContact(id: string | undefined) {
  const { gate, makeClient } = useApiClient()
  const [plan, setPlan] = useState<OutlookContactPlan | null>(null)
  const [loading, setLoading] = useState(false)
  const [error, setError] = useState<ApiError | Error | null>(null)

  const refresh = useCallback(async () => {
    if (!id || gate !== 'ready') return
    const client = makeClient()
    if (!client) return
    setLoading(true)
    setError(null)
    try {
      setPlan(await client.get<OutlookContactPlan>(`/api/personas/${id}/outlook-contact`))
    } catch (err) {
      setError(err as ApiError | Error)
      setPlan(null)
    } finally {
      setLoading(false)
    }
  }, [id, gate, makeClient])

  const apply = useCallback(async (action: 'create' | 'link') => {
    if (!id) throw new ApiError('API_REQUEST_FAILED', 'Persona no disponible')
    const client = makeClient()
    if (!client) throw new ApiError('API_UNAUTHORIZED', 'No hay sesión Microsoft')
    setLoading(true)
    setError(null)
    try {
      const result = await client.post<{ mode: 'created' | 'linked'; contact: OutlookContactPreview; verified?: boolean; code?: string }>(`/api/personas/${id}/outlook-contact`, { action })
      await refresh()
      if (!result.verified) throw new ApiError('API_REQUEST_FAILED','El resultado Microsoft no está confirmado. No vuelvas a crear el contacto.')
      return result
    } catch (err) {
      setError(err as ApiError | Error)
      throw err
    } finally {
      setLoading(false)
    }
  }, [id, makeClient, refresh])

  return { plan, loading, error, refresh, apply }
}

interface PersonaDetailState {
  status: PersonasStatus
  persona: PersonaDetail | null
  error: ApiError | Error | null
  reload: () => void
}

export function usePersonaDetail(id: string | undefined): PersonaDetailState {
  const { gate, makeClient } = useApiClient()
  const [persona, setPersona] = useState<PersonaDetail | null>(null)
  const [status, setStatus] = useState<PersonasStatus>('loading')
  const [error, setError] = useState<ApiError | Error | null>(null)
  const [attempt, setAttempt] = useState(0)

  const reload = useCallback(() => setAttempt((v) => v + 1), [])

  useEffect(() => {
    if (!id) return
    if (gate !== 'ready') return setStatus(gate)

    const controller = new AbortController()
    const client = makeClient()
    if (!client) return setStatus('not_signed_in')

    setStatus('loading')
    setError(null)
    client
      .get<{ persona: PersonaDetail }>(`/api/personas/${id}`, { signal: controller.signal })
      .then((response) => {
        setPersona(response.persona)
        setStatus('ready')
      })
      .catch((err: ApiError | Error) => {
        if (controller.signal.aborted) return
        setPersona(null)
        setError(err)
        setStatus(statusFromError(err))
      })
    return () => controller.abort()
  }, [id, gate, attempt, makeClient])

  return { status, persona, error, reload }
}
